// Helpers de pagos parciales compartidos por las vistas del operador y del comercio.
// La fuente de verdad del saldo es billing (`amountPaid` / `amountDue`); los fallbacks
// mantienen compatibles las notas viejas (`paymentStatus === 'paid'`).
import { apiErrorCode, apiErrorMessage } from '../../../../utils/apiErrorMessage';
import { formatCurrency } from './cobroHelpers';

// Métodos que acepta POST /billing/cobro/:id/payments. El abono a varias notas
// agrega `credit`, pero solo vía "Usar saldo a favor" (exige creditId).
export const PAYMENT_METHODS = ['transfer', 'cash', 'check', 'card', 'other'];

// Solo estos orígenes se pueden anular desde el panel: los del comercio/PSP se
// revierten desde su propio pago (si no, el flujo automático los resucita).
export const VOIDABLE_PAYMENT_SOURCES = [
  'operator',
  'bulk_mark',
  'allocation',
  'credit',
  'legacy',
];

export const isPaymentVoidable = (payment) =>
  !!payment && VOIDABLE_PAYMENT_SOURCES.includes(payment.source);

// Etiquetas de presentación (claves i18n) por método de pago.
export const PAYMENT_METHOD_LABELS = {
  transfer: 'Transferencia',
  cash: 'Efectivo',
  check: 'Cheque',
  card: 'Tarjeta',
  credit: 'Saldo a favor',
  other: 'Otro',
  manual_operator: 'Marcado manual',
  manual_transfer: 'Transferencia manual',
  flow: 'Flow',
  khipu: 'Khipu',
  mercadopago: 'Mercado Pago',
  transbank: 'Transbank Webpay',
  linkify: 'Linkify',
};

export const paymentMethodLabelKey = (method) => PAYMENT_METHOD_LABELS[method] ?? method ?? '';

/** Monto efectivamente pagado de una nota (`amountPaid` o, en notas viejas, `paid`). */
export const paidAmountOf = (note) => {
  if (!note) return 0;
  if (typeof note.amountPaid === 'number') return note.amountPaid;
  return note.paymentStatus === 'paid' ? Number(note.totalAmount) || 0 : 0;
};

/** Saldo pendiente de una nota (`amountDue` o total − pagado como respaldo). */
export const amountDueOf = (note) => {
  if (!note) return 0;
  if (typeof note.amountDue === 'number') return Math.max(0, note.amountDue);
  return Math.max(0, (Number(note.totalAmount) || 0) - paidAmountOf(note));
};

/** CLP se cobra en enteros; el contrato admite hasta 2 decimales en otras monedas. */
export const amountDecimals = (currency) => (currency === 'CLP' ? 0 : 2);

/** true si el monto respeta los decimales de la moneda. */
export const hasValidPrecision = (amount, currency) => {
  const factor = 10 ** amountDecimals(currency);
  // Epsilon: 1.23 * 100 da 123.00000000000001 y la comparación exacta rechazaría
  // un monto de 2 decimales perfectamente válido.
  return Math.abs(Math.round(amount * factor) - amount * factor) < 1e-9;
};

/** Tolerancia de redondeo del contrato (§1): max(2, ceil(total × 2,4e-6)). */
export const roundingTolerance = (total) =>
  Math.max(2, Math.ceil((Number(total) || 0) * 2.4e-6));

/** Convierte el valor de un input (acepta coma decimal) a número, o NaN. */
export const parseAmountInput = (value) => {
  const normalized = String(value ?? '').trim().replace(',', '.');
  if (!normalized) return NaN;
  return Number(normalized);
};

const pad2 = (n) => String(n).padStart(2, '0');
const ymdOf = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

/** Día local de hoy en 'YYYY-MM-DD'. */
export const todayYMD = () => ymdOf(new Date());

/** Día local de mañana en 'YYYY-MM-DD' (límite del contrato para `paidAt`). */
export const tomorrowYMD = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return ymdOf(date);
};

// El operador elige un DÍA; se manda la medianoche local de ese día como ISO-8601. Para hoy o
// mañana siempre cae dentro del límite "≤ ahora + 1 día" del contrato.
export const paidAtIso = (ymd) => new Date(`${ymd}T00:00:00`).toISOString();

/** Referencias vigentes de la nota, para el banner de "pagada". */
export const paidReferencesOf = (note) => {
  const fromPayments = (note?.payments ?? [])
    .filter((payment) => !payment.voidedAt && payment.reference)
    .map((payment) => payment.reference);
  if (fromPayments.length) return fromPayments;
  return note?.paymentReference ? [note.paymentReference] : [];
};

/** Quién registró el pago, sin exponer el ObjectId crudo en la tabla. */
export const registeredByLabelKey = (payment) => {
  if (!payment?.registeredBy) return null;
  if (payment.registeredBy === 'system') return 'Sistema';
  if (typeof payment.registeredBy === 'object') {
    return payment.registeredBy.name || payment.registeredBy.email || 'Operador';
  }
  return 'Operador';
};

const CODE_MESSAGES = {
  NOT_PAYABLE: 'La nota no admite pagos en su estado actual.',
  DUPLICATE_REFERENCE: 'Ya existe un pago con esa referencia en esta nota.',
  CONCURRENT_UPDATE: 'Otro usuario actualizó la nota. Vuelve a intentarlo.',
  PAYMENT_NOT_VOIDABLE: 'Este pago se revierte desde el pago del comercio.',
  ALREADY_VOIDED: 'Este pago ya fue anulado.',
  MIXED_DEBTOR: 'Las notas deben ser de un solo deudor.',
  MIXED_CURRENCY: 'Las notas deben estar en la misma moneda.',
  INSUFFICIENT_CREDIT: 'El saldo a favor no es suficiente para este abono.',
};

/**
 * Mensaje claro para los 409/422 del contrato. Usa el texto del backend cuando no
 * hay un código conocido y, si tampoco hay texto útil, el fallback del llamador.
 * @param {unknown} err
 * @param {(key: string, opts?: object) => string} t
 * @param {string} [fallback]
 * @param {string} [currency] moneda de la nota, para formatear el saldo del 422
 */
export const paymentErrorMessage = (err, t, fallback, currency = 'CLP') => {
  const code = apiErrorCode(err);
  if (code === 'OVERPAYMENT') {
    const due = err?.response?.data?.amountDue;
    return typeof due === 'number'
      ? t(
          'El monto supera el saldo pendiente ({{amount}}). Usa «Registrar abono a varias notas» si quieres dejar el excedente como saldo a favor.',
          { amount: formatCurrency(due, currency) },
        )
      : t('El monto supera el saldo pendiente.');
  }
  if (code && CODE_MESSAGES[code]) return t(CODE_MESSAGES[code]);
  return apiErrorMessage(err, fallback) ?? fallback;
};
